import React from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import styled, { keyframes, css } from 'styled-components';
import { FiGlobe, FiCheck, FiInfo, FiMapPin } from 'react-icons/fi';
import ReactCountryFlag from 'react-country-flag';
import { theme } from '../styles/theme';
import { sortCountriesWithPreference } from '../utils/countryPreferences';

// Country data with country codes for flag icons
// Restricted to specific countries only
export const COUNTRIES = [
  { code: 'MA', name: 'Morocco', countryCode: 'MA', fullName: 'Kingdom of Morocco' },
  { code: 'EG', name: 'Egypt', countryCode: 'EG', fullName: 'Arab Republic of Egypt' },
  { code: 'SA', name: 'Saudi Arabia', countryCode: 'SA', fullName: 'Kingdom of Saudi Arabia' },
  { code: 'AE', name: 'UAE', countryCode: 'AE', fullName: 'United Arab Emirates' },
  { code: 'QA', name: 'Qatar', countryCode: 'QA', fullName: 'State of Qatar' },
  { code: 'KW', name: 'Kuwait', countryCode: 'KW', fullName: 'State of Kuwait' },
  { code: 'OM', name: 'Oman', countryCode: 'OM', fullName: 'Sultanate of Oman' }
];

// Pulsing animation for error state
const errorPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    border-color: rgba(239, 68, 68, 0.5);
  }
`;

// Country Selector Container
const CountrySelectorContainer = styled(motion.div)`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const CountryCard = styled(motion.button)`
  background: ${({ $hasError, $selected, theme }) =>
    $hasError
      ? theme.colors.background.card
      : $selected
        ? theme.colors.background.card
        : theme.colors.background.card};
  border: 2px solid ${({ $hasError, $selected, theme }) =>
    $hasError
      ? theme.colors.border.error
      : $selected
        ? theme.colors.success
        : theme.colors.border.default};
  opacity: 1;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;
  overflow: hidden;
  width: 100%;
  text-align: left;
  box-shadow: ${({ theme }) => theme.shadows.subtle};

  ${({ $hasError }) => $hasError && css`
    animation: ${errorPulse} 2s infinite ease-in-out;
  `}

  &:focus-visible {
    outline: none;
    border-color: ${({ $hasError, theme }) =>
    $hasError
      ? theme.colors.text.error
      : theme.colors.primary};
    box-shadow: ${({ $hasError, theme }) =>
    $hasError
      ? `0 0 0 3px ${theme.colors.border.error}`
      : `0 0 0 3px ${theme.colors.success}`};
  }
`;

const CountryIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.gradient.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 20px;
  flex-shrink: 0;
`;

const FlagIcon = styled.div`
  width: 32px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
`;

const CountryDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: 2px;
`;

const CountryName = styled.h4`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0;
  text-align: left;
  line-height: 1.2;
`;



const CountryFullName = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
  text-align: left;
`;

const PlaceholderText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const PlaceholderSubtext = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  opacity: 0.6;
`;



const Checkmark = styled(motion.div)`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`;

const EmptyCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.card};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  flex-shrink: 0;
`;


// Country Popup Components (similar to calendar/duration popups)
const CountryPopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* Use dynamic viewport height for mobile */
  background: rgba(0, 0, 0, 0.85); /* More opaque for better coverage */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 20px;

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: 0;
    background: rgba(15, 23, 42, 1); /* Fully opaque on mobile */
  }
`;

const CountryPopupContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.gradient.card};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 600px; /* Aligned with main app card */
  width: 90%;
  max-height: 85vh;
  backdrop-filter: blur(12px);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
  }

  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    max-height: 100dvh; /* Use dynamic viewport height for mobile */
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: ${({ theme }) => theme.borderRadius.xl};
    border-top-right-radius: ${({ theme }) => theme.borderRadius.xl};
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const CountryPopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`;

const CountryPopupTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0;
  text-align: center;
  flex: 1;
`;

const CountryPopupDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0 0 24px 0;
  line-height: 1.5;
  opacity: 0.8;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const CountryPopupGrid = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.success};
  }
`;

const CountryPopupCard = styled(motion.button)`
  background: ${({ theme }) => theme.colors.background.card};
  border: 2px solid ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.success : theme.colors.border.default};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => theme.spacing.lg}; /* Reduced from xl */
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: all ${({ theme }) => theme.transitions.normal};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  text-align: left;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  position: relative;
  overflow: hidden;
  min-height: 80px;

  &:hover {
    border-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.success : theme.colors.primary};
    box-shadow: ${({ $isSelected, theme }) =>
    $isSelected ? theme.shadows.subtle : theme.shadows.prominent};
    transform: ${({ $isSelected }) => $isSelected ? 'none' : 'translateY(-2px)'};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.success : theme.colors.primary};
    box-shadow: ${({ $isSelected, theme }) =>
    $isSelected
      ? `0 0 0 3px ${theme.colors.success}20`
      : `0 0 0 3px ${theme.colors.primary}20`};
  }
`;

const CountryPopupFlag = styled.div`
  width: 40px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
`;

const CountryPopupDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 12px;
  text-align: center;
`;

const CountryPopupName = styled.h4`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0;
  text-align: center;
  line-height: 1.2;
`;


const CountryPopupActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
  width: 100%;
`;

const LocationInfoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  opacity: 0.85;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
  
  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.xs || '8px'};
    padding: ${({ theme }) => theme.spacing.xs};
  }
`;

const LocationIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
  }
`;

const LocationTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const LocationContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-width: 0;
  flex: 1;
`;

const LocationTopLine = styled.div`
  color: #10b981;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  line-height: 1.5;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs || '12px'};
    gap: 6px;
  }
`;

const AutoLocationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: #10b981;
  white-space: nowrap;
  opacity: 0.95;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(16, 185, 129, 0.1);
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs || '12px'};
    padding: 1px 4px;
  }
`;

const LocationBottomLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: #10b981;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  line-height: 1.5;
  text-align: center;
  opacity: 0.9;
  width: 100%;
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs || '12px'};
    gap: 6px;
  }
`;

const IPAddress = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamily.mono || 'monospace'};
  letter-spacing: 0.5px;
`;

const InfoIcon = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #3B82F6;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  opacity: 0.75;
  padding: 0;
  position: relative;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.4);
    opacity: 1;
    transform: scale(1.15);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid rgba(59, 130, 246, 0.5);
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
    font-size: 10px;
  }
`;

const CountryPopupButton = styled(motion.button)`
  width: 100%;
  padding: 14px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  ${({ $primary, theme }) => $primary ? `
    background: ${theme.colors.gradient.primary};
    color: white;
    border: none;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
      filter: brightness(1.1);
    }
  ` : `
    background: transparent;
    color: ${theme.colors.text.error};
    border: 1px solid rgba(239, 68, 68, 0.4);
    
    &:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.05);
      border-color: ${theme.colors.text.error};
      transform: translateY(-1px);
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(0);
    filter: brightness(0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CountrySelectorComponent = ({ selectedCountry, onCountryChange, currentStep, completedSteps, externalOpen = false, onPopupClose, location }) => {
  const [showCountryPopup, setShowCountryPopup] = React.useState(false);
  const [tempSelectedCountry, setTempSelectedCountry] = React.useState(null);

  // Allow parent to externally trigger opening the popup (e.g., from review modal)
  React.useEffect(() => {
    if (externalOpen) {
      setTempSelectedCountry(selectedCountry);
      setShowCountryPopup(true);
    }
  }, [externalOpen, selectedCountry]);

  const handleCountrySelect = (country) => {
    setTempSelectedCountry(country);
  };

  const handleOpenCountryPopup = () => {
    setTempSelectedCountry(selectedCountry); // Set current selection as temp
    setShowCountryPopup(true);
  };

  const handleCloseCountryPopup = () => {
    setTempSelectedCountry(null);
    setShowCountryPopup(false);
    if (onPopupClose) onPopupClose();
  };

  const handleConfirmCountrySelection = () => {
    if (tempSelectedCountry) {
      onCountryChange(tempSelectedCountry);
    }
    setShowCountryPopup(false);
    if (onPopupClose) onPopupClose();
  };

  return (
    <CountrySelectorContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <CountryCard
        $selected={!!selectedCountry}
        $hasError={!selectedCountry && currentStep === 'country'}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          handleOpenCountryPopup();
        }}
        type="button"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        aria-haspopup="dialog"
        aria-expanded={showCountryPopup}
        aria-label="Select country"
      >
        {selectedCountry ? (
          <>
            <FlagIcon>
              <ReactCountryFlag
                countryCode={selectedCountry.countryCode}
                svg
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </FlagIcon>
            <CountryDetails>
              <CountryName>
                {selectedCountry.name}
              </CountryName>
              <CountryFullName>Issuing Authority</CountryFullName>
            </CountryDetails>
            <Checkmark
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <FiCheck size={12} />
            </Checkmark>
          </>
        ) : (
          <>
            <CountryIcon>
              <FiGlobe size={20} />
            </CountryIcon>
            <CountryDetails>
              <PlaceholderText>Select Country</PlaceholderText>
              <PlaceholderSubtext>Select the country that issued your visa.</PlaceholderSubtext>
            </CountryDetails>
            <EmptyCircle />
          </>
        )}
      </CountryCard>

      {/* Required Field Message */}
      {!selectedCountry && (
        <>
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              color: theme.colors.text.error,
              fontSize: '14px',
              marginTop: '8px',
              marginBottom: '24px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <div style={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: theme.colors.text.error
            }} />
            Please select a country to continue
          </motion.div>
          
          {/* Location Info */}
          {location && (location.userCity || location.userCountryName || location.userCountryCode || location.detectedCountryCode || location.city || location.country || location.countryCode || location.userIP || location.ip) && (
            <LocationInfoContainer
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {(() => {
                // Check both transformed field names and raw field names from service
                const city = location.userCity || location.city;
                const country = location.userCountryName || location.detectedCountry || location.country;
                const countryCode = location.userCountryCode || location.detectedCountryCode || location.countryCode;
                const ip = location.userIP || location.ip;
                
                let locationText = '';
                // Prioritize showing countryCode when available, even without city
                if (city && countryCode) {
                  locationText = `${city}, ${countryCode.toUpperCase()}`;
                } else if (countryCode) {
                  // Show countryCode even if city is missing (e.g., Morocco "MA")
                  locationText = countryCode.toUpperCase();
                  // Add country name if available for better context
                  if (country && country !== countryCode) {
                    locationText = `${countryCode.toUpperCase()} (${country})`;
                  }
                } else if (city && country) {
                  locationText = `${city}, ${country}`;
                } else if (country) {
                  locationText = country;
                } else if (city) {
                  locationText = city;
                }
                
                // Always show something if we have any data (location or IP)
                if (!locationText && !ip) {
                  return null;
                }
                
                return (
                  <>
                    <LocationTextContainer>
                      <LocationIcon>
                        <FiMapPin size={18} />
                      </LocationIcon>
                      <LocationContentWrapper>
                        <LocationTopLine>
                          {locationText ? (
                            <>
                              <span>{locationText}</span>
                              {ip && (
                                <>
                                  <span style={{ color: '#10b981', opacity: 0.7 }}>•</span>
                                  <span style={{ color: '#10b981' }}>IP: {ip}</span>
                                </>
                              )}
                            </>
                          ) : ip ? (
                            <span style={{ color: '#10b981' }}>IP: {ip}</span>
                          ) : null}
                        </LocationTopLine>
                      </LocationContentWrapper>
                    </LocationTextContainer>
                  </>
                );
              })()}
            </LocationInfoContainer>
          )}
        </>
      )}

      {/* Country Selection Popup (Portal) */}
      {showCountryPopup &&
        ReactDOM.createPortal(
          (
            <CountryPopupOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseCountryPopup}
            >
              <CountryPopupContainer
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <CountryPopupHeader>
                  <CountryPopupTitle>Select Country</CountryPopupTitle>
                </CountryPopupHeader>

                <CountryPopupDescription>
                  Select the country that issued your visa from the list below
                </CountryPopupDescription>

                <CountryPopupGrid
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  {sortCountriesWithPreference(COUNTRIES).map((country, index) => (
                    <CountryPopupCard
                      key={country.code}
                      onClick={() => handleCountrySelect(country)}
                      $isSelected={tempSelectedCountry?.code === country.code}
                      initial={{
                        opacity: 0,
                        scale: 0.95
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1
                      }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.08,
                        ease: [0.4, 0.0, 0.2, 1]
                      }}
                    >
                      <CountryPopupDetails>
                        <CountryPopupFlag>
                          <ReactCountryFlag
                            countryCode={country.countryCode}
                            svg
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </CountryPopupFlag>
                        <CountryPopupName>
                          {country.name}
                        </CountryPopupName>
                      </CountryPopupDetails>
                      {tempSelectedCountry?.code === country.code && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: theme.colors.success,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}
                        >
                          <FiCheck size={10} />
                        </motion.div>
                      )}
                    </CountryPopupCard>
                  ))}
                </CountryPopupGrid>

                <CountryPopupActions>
                  <CountryPopupButton
                    $primary
                    onClick={handleConfirmCountrySelection}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!tempSelectedCountry}
                    style={{
                      opacity: tempSelectedCountry ? 1 : 0.5,
                      cursor: tempSelectedCountry ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Confirm
                  </CountryPopupButton>
                  <CountryPopupButton
                    onClick={handleCloseCountryPopup}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </CountryPopupButton>
                </CountryPopupActions>
              </CountryPopupContainer>
            </CountryPopupOverlay>
          ),
          document.body
        )}

    </CountrySelectorContainer>
  );
};

// Export memoized version to prevent unnecessary re-renders
export const CountrySelector = React.memo(CountrySelectorComponent);
